import React from "react";
import {
  withStyles,
  Divider,
  Grid,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { observer, inject } from "mobx-react";
import { DropdownEditor } from '@dataGrid/components/cellEditors';
import StyledChip from "./StyledChip";
import Events from "../../../../../../utilities/EventEmitter";
import Detail from "./Detail";
import { getUnassignedPickupDetails, getDriverDetails } from "./utils";
import {assignPickup, createAssignPickupDetails} from "../../utils/pickupActions";
import { withSnackbar } from "notistack";
import { compose } from "recompose";

const styles = (theme) => ({
  container: {
    minWidth: 360,
    maxWidth: 360,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flexGrow: 1,
    overflow: "auto",
  },
  divider: {
    backgroundColor: theme.AppBGColor,
  },
  head: {
    fontSize: 14,
    color: theme.HeaderSelectedText,
    padding: "8px 24px",
    fontWeight: 600,
    flexBasis: "auto",
  },
  pickupContainer: {
    padding: "8px 0px",
  },
  detailContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  dataContainer: {
    padding: "8px 24px",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.gridTextColor,
  },
  driverNameContainer: {
    display: "flex",
    paddingTop: 16,
    paddingBottom: 16,
  },
  required: {
    color: theme.errorTextColor,
  },
  driverNameLabel: {
    width: "40%",
    minWidth: "16ch",
  },
  driverNameEditor: {
    width: "100%",
    "& .select__control": {
      marginTop: 0,
      minHeight: 30,
      borderRadius: 4,
      borderColor: theme.gridTextColor,
      "& .select__indicators": {
        height: 28,
      },
      "&:hover,&.select__control--is-focused": {
        borderRadius: 4,
      },
    },
  },
  footer: {
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
  },
  cancelButton: {
    minWidth: "40%",
  },
  assignPickupButton: {
    minWidth: "55%",
    "&$disabledButton": {
      backgroundColor: theme.customInputBGColor,
      color: theme.TextColor,
      opacity: 0.8
    },
  },
  disabledButton: {
  },
  circularProgress: {
    color: theme.white,
  },
  completeAssignment: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  completeAssignmentLabel: {
    color: theme.TextColor,
    paddingBottom: 16,
  },
});

const PICKUP_ASSIGNMENT_STATUS = {
  PENDING: 1,
  INPROGRESS: 2,
  COMPLETE: 3,
};

@inject("DispatchStore")
@observer
class PickupAssignmentLasso extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedPickups: this.props.unassignedPickups,
      selectedDriverId: null,
      assignmentStatus: PICKUP_ASSIGNMENT_STATUS.PENDING,
    };
  }

  componentDidMount() {
    Events.on("PickupAssignmentCompleted", this.pickupAssignmentComplete);
  }

  componentWillUnmount() {
    Events.off("PickupAssignmentCompleted", this.pickupAssignmentComplete);
  }

  componentDidUpdate(prevProps) {
    if (this.props.unassignedPickups !== prevProps.unassignedPickups) {
      this.setState({
        selectedPickups: this.props.unassignedPickups,
      });
    }
  }

  removePickup = (pickupNumber) => () => {
    this.setState((state) => ({
      selectedPickups: state.selectedPickups.filter(
        (pickup) => pickup.pickupNumber !== pickupNumber
      ),
    }));
  };

  selectDriver = (data) => {
    this.setState({
      selectedDriverId: data?.value ?? null,
    });
  };

  assignPickup = () => {
  
    this.setState({
      assignmentStatus: PICKUP_ASSIGNMENT_STATUS.INPROGRESS,
    });

    const assignPickupDetails = createAssignPickupDetails(this.state.selectedPickups, {driverId: this.state.selectedDriverId});
    assignPickup({
      assignPickupDetails,
      terminalCode: this.props.userProfile.terminal.code,
      glEventHub: this.props.glEventHub,
      DispatchStore: this.props.DispatchStore,
      enqueueSnackbar: this.props.enqueueSnackbar
    }).catch(() => {
      this.setState({
        assignmentStatus: PICKUP_ASSIGNMENT_STATUS.PENDING,
      });
    });
  };

  pickupAssignmentComplete = () => {
    this.setState({
      assignmentStatus: PICKUP_ASSIGNMENT_STATUS.COMPLETE,
    });
  };

  render() {
    const { classes } = this.props;
    const { selectedPickups, assignmentStatus, selectedDriverId } = this.state;
    const unassignedPickupDetails = getUnassignedPickupDetails(selectedPickups);
    const { driverStopDetails, driverPendingPickups } = getDriverDetails(
      this.props.DispatchStore.getDriverRoutes(selectedDriverId)
    );
    const assignPickupButtonStatus =
      assignmentStatus === PICKUP_ASSIGNMENT_STATUS.PENDING ? (
        "Assign Pickups"
      ) : (
        <CircularProgress
          size={16}
          classes={{ root: classes.circularProgress }}
        />
      );
    return (
      <div className={classes.container}>
        <div>
          <Divider className={classes.divider} />
          <Grid item xs={12} className={classes.head}>
            Pickup Assignment
          </Grid>
          <Divider className={classes.divider} />
        </div>
        {assignmentStatus === PICKUP_ASSIGNMENT_STATUS.COMPLETE ? (
          <div className={classes.completeAssignment}>
            <div>
              <div className={classes.completeAssignmentLabel}>
                Pickup assignment completed
              </div>
              <Button
                color="primary"
                variant="contained"
                onClick={this.props.close}
                fullWidth
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className={`${classes.content} customScroll`}>
              <Grid item xs={12} className={classes.dataContainer}>
                <label className={classes.unassignedPickupLabel}>
                  Select unassigned pickups
                </label>
                <div className={classes.pickupContainer}>
                  {selectedPickups.map((pickup) => (
                    <StyledChip
                      key={pickup.pickupNumber}
                      label={`${pickup.shipperName} (${pickup.pickupNumber})`}
                      remove={this.removePickup(pickup.pickupNumber)}
                    />
                  ))}
                </div>
                <div className={classes.detailContainer}>
                  {unassignedPickupDetails.map((detail) => (
                    <Detail
                      label={detail.label}
                      value={detail.value || "-"}
                      key={detail.label}
                    />
                  ))}
                </div>
              </Grid>

              <Divider className={classes.divider} />
              <Grid item xs={12} className={classes.head}>
                Driver Details
              </Grid>
              <Divider className={classes.divider} />
              <div
                className={`${classes.dataContainer} ${classes.driverNameContainer}`}
              >
                <div className={classes.driverNameLabel}>
                  DriverName<sup className={classes.required}>*</sup>
                </div>
                <div className={classes.driverNameEditor}>
                  <DropdownEditor
                    column={{
                      colDef: {
                        field: "dispatchedDriversList",
                        cellEditorParams: {
                          isClearable: false,
                        },
                      },
                    }}
                    onChange={this.selectDriver}
                  />
                </div>
              </div>
              {this.state.selectedDriverId && (
                <Grid item xs={12} className={classes.dataContainer}>
                  <label className={classes.unassignedPickupLabel}>
                    Existing Pickups
                  </label>
                  <div className={classes.pickupContainer}>
                    {driverPendingPickups.map((pickup) => (
                      <StyledChip
                        key={pickup.pickupNumber}
                        label={`${pickup.name} (${pickup.pickupNumber})`}
                      />
                    ))}
                  </div>
                  <div className={classes.detailContainer}>
                    {driverStopDetails.map((detail) => (
                      <Detail
                        label={detail.label}
                        value={detail.value || "-"}
                        key={detail.label}
                      />
                    ))}
                  </div>
                </Grid>
              )}
            </div>
            <div className={classes.footer}>
              <Button
                color="primary"
                variant="outlined"
                className={classes.cancelButton}
                onClick={this.props.close}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  !(
                    this.state.selectedPickups.length &&
                    this.state.selectedDriverId
                  )
                }
                classes={{
                  root: classes.assignPickupButton,
                  disabled: classes.disabledButton,
                }}
                onClick={this.assignPickup}
              >
                {assignPickupButtonStatus}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default compose(withSnackbar,withStyles(styles))(PickupAssignmentLasso);
